import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get as getRequest, remove as removeRequest, update as updateRequest } from './requests';
import { toast } from 'react-toastify';
import { PythonFunction, QueryKey, UpdatePythonFunctionRequest } from 'api/model';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.PythonFunctions;

type Props = {
  functionId: string;
  revision?: number;
  pythonFunction?: PythonFunction;
  enabled?: boolean;
};

const usePythonFunction = ({ functionId, revision, pythonFunction: initialFunction, enabled = true }: Props) => {
  const queryClient = useQueryClient();

  const {
    data: latestFunction,
    isLoading: functionIsLoading,
    isFetched: functionIsFetched,
  } = useQuery({
    queryKey: [queryKey, { id: functionId, revision }],
    queryFn: () => getRequest(functionId, revision),
    enabled: enabled && !!functionId && !initialFunction,
  });

  const pythonFunction = latestFunction ?? initialFunction;

  const update = useMutation({
    mutationFn: (model: UpdatePythonFunctionRequest) => updateRequest(functionId, model),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.PythonFunctionFolders] });
      toast.success('Python function successfully updated');
    },
    onError: errorHandler('Updating python function failed'),
  });

  const remove = useMutation({
    mutationFn: () => removeRequest(functionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.PythonFunctionFolders] });
      toast.success('Python function successfully removed');
    },
    onError: errorHandler('Removing python function failed'),
  });

  return { pythonFunction, functionIsLoading, functionIsFetched, remove, update };
};

export default usePythonFunction;
